/**
 * Hooks React Query da chamada de vídeo do atendente.
 *
 * Erros chegam como `VideoCallApiError` (`error.code`, `error.reason`) — ver `@/services/videoCallApi`.
 * Mutações que mudam o estado da chamada invalidam opções, histórico e a chamada ativa: o botão
 * (`blockedReason=AGENT_IN_CALL`) e a lista refletem a chamada nova ou encerrada.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    admitGuest,
    deleteVideoCallRecording,
    endVideoCall,
    getVideoCallDetail,
    getVideoCallHistory,
    getVideoCallOptions,
    getVideoCallRecording,
    getVideoCallRecordingUrl,
    getVideoCallTranscript,
    leaveNoOneJoined,
    rejectGuest,
    rejoinVideoCall,
    startVideoCall,
    submitQualitySurvey,
} from '@/services/videoCallApi';
import { pollUnlessClientError } from '@/lib/apiFailure';

export const VIDEO_CALL_QUERY_KEYS = Object.freeze({
    all: ['video-calls'],
    options: (contactId, ticketId) => ['video-calls', 'options', contactId, ticketId ?? null],
    history: (filters) => ['video-calls', 'history', filters ?? {}],
    detail: (id) => ['video-calls', 'detail', id],
    transcript: (id) => ['video-calls', 'transcript', id],
    recordings: (id) => ['video-calls', 'recordings', id],
    active: ['video-calls', 'active'],
});

function useInvalidateVideoCalls() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: VIDEO_CALL_QUERY_KEYS.all });
}

/**
 * Opções de envio do contato. Sempre frescas: disponibilidade de canal e saldo mudam a qualquer hora.
 * @param {{ contactId?: string|null, ticketId?: string|null, contactless?: boolean, enabled?: boolean }} params
 *   `contactless`: as opções de uma sala sem contato (só o link); sem ele a consulta espera o `contactId`.
 */
export function useVideoCallOptions({ contactId, ticketId = null, contactless = false, enabled = true }) {
    return useQuery({
        queryKey: VIDEO_CALL_QUERY_KEYS.options(contactId ?? null, ticketId),
        queryFn: () => getVideoCallOptions({ contactId, ticketId }),
        enabled: (Boolean(contactId) || contactless) && enabled,
        staleTime: 0,
        retry: false,
    });
}

export function useStartVideoCall() {
    const invalidate = useInvalidateVideoCalls();
    return useMutation({ mutationFn: (variables) => startVideoCall(variables), onSuccess: invalidate });
}

export function useRejoinVideoCall() {
    return useMutation({ mutationFn: (variables) => rejoinVideoCall(variables) });
}

export function useEndVideoCall() {
    const invalidate = useInvalidateVideoCalls();
    return useMutation({ mutationFn: (variables) => endVideoCall(variables), onSuccess: invalidate });
}

export function useLeaveNoOneJoined() {
    const invalidate = useInvalidateVideoCalls();
    return useMutation({ mutationFn: (variables) => leaveNoOneJoined(variables), onSuccess: invalidate });
}

export function useAdmitGuest() {
    return useMutation({ mutationFn: (variables) => admitGuest(variables) });
}

export function useRejectGuest() {
    return useMutation({ mutationFn: (variables) => rejectGuest(variables) });
}

export function useSubmitQualitySurvey() {
    return useMutation({ mutationFn: (variables) => submitQualitySurvey(variables) });
}

/**
 * Histórico keyset: cada página traz `nextCursor`; `null` encerra a lista.
 * @param {object} [filters] - `{ from, to, agentId, contactId, status: string[], deliveryMethod, q }`
 * @param {{ refetchInterval?: number|false, enabled?: boolean }} [options] `refetchInterval`: reler de tempos em tempos
 *   (a lista das salas abertas acompanha a sala sem esperar um evento). A releitura PARA quando a consulta falha por erro
 *   do cliente (sem licença, sem permissão, plano sem o recurso): repetir não resolve e só enche o servidor de recusas.
 */
export function useVideoCallHistory(filters = {}, { refetchInterval = false, enabled = true } = {}) {
    return useInfiniteQuery({
        queryKey: VIDEO_CALL_QUERY_KEYS.history(filters),
        queryFn: ({ pageParam }) => getVideoCallHistory({ filters, cursor: pageParam }),
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
        refetchInterval: typeof refetchInterval === 'number' ? pollUnlessClientError(refetchInterval) : refetchInterval,
        enabled,
    });
}

/**
 * Detalhe de uma chamada (participantes, consentimento, link do criador). Sala não terminal: relê de tempos em tempos
 * para o estado e os participantes acompanharem a sala.
 * @param {string|null} videoCallId
 * @param {{ refetchInterval?: number|false }} [options]
 */
export function useVideoCallDetail(videoCallId, { refetchInterval = false } = {}) {
    return useQuery({
        queryKey: VIDEO_CALL_QUERY_KEYS.detail(videoCallId),
        queryFn: () => getVideoCallDetail(videoCallId),
        enabled: Boolean(videoCallId),
        refetchInterval,
        retry: false,
    });
}

/**
 * Texto da chamada (falas por pessoa e momentos). Só pede quando a chamada foi transcrita.
 * @param {string|null} videoCallId
 * @param {boolean} enabled
 */
export function useVideoCallTranscript(videoCallId, enabled) {
    return useQuery({
        queryKey: VIDEO_CALL_QUERY_KEYS.transcript(videoCallId),
        queryFn: () => getVideoCallTranscript(videoCallId),
        enabled: Boolean(videoCallId) && enabled,
        retry: false,
        staleTime: 30_000,
    });
}

/** Uma gravação ainda não terminou: o card relê de tempos em tempos até ela ficar pronta (ou falhar). */
export const RECORDINGS_POLL_MS = 10_000;
const UNFINISHED = new Set(['STARTING', 'RECORDING', 'PROCESSING']);

/**
 * As gravações da chamada (estado, validade, se pode baixar e apagar). Relê sozinha enquanto alguma ainda está sendo feita.
 * @param {string|null} videoCallId
 */
export function useVideoCallRecordings(videoCallId) {
    return useQuery({
        queryKey: VIDEO_CALL_QUERY_KEYS.recordings(videoCallId),
        queryFn: () => getVideoCallRecording(videoCallId),
        enabled: Boolean(videoCallId),
        retry: false,
        staleTime: 30_000,
        refetchInterval: (query) => (query.state.data?.recordings?.some((r) => UNFINISHED.has(r.status)) ? RECORDINGS_POLL_MS : false),
    });
}

/** URL assinada (15 min) para assistir ou baixar. Cada pedido gera uma URL nova: nunca é guardada em cache. */
export function useVideoCallRecordingUrl() {
    return useMutation({ mutationFn: (variables) => getVideoCallRecordingUrl(variables) });
}

/** Apaga a gravação e relê a lista. */
export function useDeleteVideoCallRecording() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables) => deleteVideoCallRecording(variables),
        onSuccess: (_data, { videoCallId }) => queryClient.invalidateQueries({ queryKey: VIDEO_CALL_QUERY_KEYS.recordings(videoCallId) }),
    });
}

